<template>
	<ManageService
		v-if="showManageServiceScreen"
		:cookie-script-to-edit="serviceToEdit"
		:cookie-script-channel-id="selectedCookieScriptChannelId"
		:accessed-via-cookie-banner-configuration="false"
		:accessed-via-collection-template="true"
		:cookie-category-id="selectedCategoryId"
		:cookie-collection-brand-id="cookieCollectionTemplate.brandId"
		@submit="submitService"
		@close="closeManageServiceScreen"
	/>
	<ValidationForm
		v-else
		ref="validationForm"
		#default="{ handleSubmit }"
		tag="div"
		class="cassie-vertical-md"
	>
		<CookieTemplateLayout
			:header-title="'Configure Collection Template' | useLabels('Configure Collection Template')"
			:header-caption="'Configure a collection of categories and scripts' | useLabels('Configure a collection of categories and scripts')"
		>
			<template #content>
				<Message
					v-if="cookieCollectionTemplate.isSystem && !cloneMode"
					type="warning"
				>
					You cannot edit system default Collection Templates. Please ensure they meet your business needs before use or clone this and edit accordingly.
				</Message>
				<Message
					v-if="mspaValidationFailed"
					type="error"
				>
					MSPA is enabled but no MSPA categories are selected. Please select one MSPA category to continue.
				</Message>
				<SectionCard>
					<template #title>
						Details
					</template>
					<template #body>
						<v-row dense>
							<v-col cols="6">
								<TextField
									v-model="cookieCollectionTemplate.name"
									label="Name *"
									:disabled="!userCanCreateUpdate || (cookieCollectionTemplate.isSystem && !cloneMode)"
									:rules="{required: true, max: 255}"
								/>
							</v-col>
							<v-col cols="6">
								<Dropdown
									v-model="cookieCollectionTemplate.brandId"
									:disabled="isEdit || cloneMode || !userCanCreateUpdate || !clientMultiBranded || singleBrand || (cookieCollectionTemplate.isSystem && !cloneMode)"
									:items="brandItems"
									custom-sort
									:label="'Brand *' | useLabels"
									searchable
									:rules="{ required: clientMultiBranded}"
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="12">
								<Textarea
									v-model="cookieCollectionTemplate.description"
									label="Description *"
									no-resize
									:disabled="!userCanCreateUpdate || (cookieCollectionTemplate.isSystem && !cloneMode)"
									:rules="{required: true, max: 500}"
								/>
							</v-col>
						</v-row>
						<v-row v-if="cassieIdentityServiceEnabled">
							<v-col cols="3">
								<Toggle
									:value.sync="cookieCollectionTemplate.crossDomainConsent"
									label="Enable Cross Domain Consent"
									tooltip-text="This collection, once submitted will enable cookies to be accessed from one of the allowed domains. The specification of this is managed in the Banner configuration page"
									:disabled="!userCanCreateUpdate || (cookieCollectionTemplate.isSystem && !cloneMode)"
								/>
							</v-col>
							<v-col cols="3">
								<Toggle
									:value.sync="cookieCollectionTemplate.mspaEnabled"
									label="Generate IAB GPP MSPA Signal"
									tooltip-text="When this collection is used as part of a consent banner the banner will create the Global Privacy Platform String and set the Multi-State Privacy Agreement section within that string as per the configuration of this collection. Additionally, the banner will load the relevant GPP stub to allow your service partners to access this string."
									:disabled="!userCanCreateUpdate || (cookieCollectionTemplate.isSystem && !cloneMode) || !mspaCategoriesExist"
								/>
							</v-col>
						</v-row>
					</template>
				</SectionCard>
				<CookieCategoriesCard
					v-if="cookieCategories.length"
					:cookie-categories="cookieCategories"
					:cookie-scripts="cookieScripts"
					:cookie-collection-template.sync="cookieCollectionTemplate"
					:brand-id.sync="cookieCollectionTemplate.brandId"
					:is-edit="isEdit"
					:user-can-create-update="userCanCreateUpdate && !(cookieCollectionTemplate.isSystem && !cloneMode)"
					@update:cookieCategories="updateCookieCollectionTemplate('cookieCategories', $event)"
					@update:cookieScripts="updateCookieCollectionTemplate('cookieScripts', $event)"
					@update:runOrderScripts="updateCookieCollectionTemplate('runOrderScripts', $event)"
					@load-cookie-categories="loadCookieCategories"
				/>
				<CookieScriptsInCategoryCard
					v-for="cookieCategory in cookieCollectionTemplate.cookieCategories"
					:key="cookieCategory.id"
					:cookie-collection-template.sync="cookieCollectionTemplate"
					:cookie-category="assignedCookieCategory(cookieCategory.id)"
					:cookie-category-scripts="cookieCategoryScripts(cookieCategory.id)"
					:cookie-categories="cookieCategories"
					:transaction-types="transactionTypes"
					:security-levels="securityLevels"
					:channel-device-copy-actions="channelDeviceCopyActions"
					:all-cookie-scripts="cookieScripts"
					:user-can-create-update="userCanCreateUpdate && !(cookieCollectionTemplate.isSystem && !cloneMode)"
					:service-just-added="serviceJustAdded"
					:gtm-consent-types="gtmConsentTypes"
					:gtm-scripts-in-use="gtmScriptsInUse"
					@update:cookieScripts="updateCookieCollectionTemplate('cookieScripts', $event)"
					@update:runOrderScripts="updateCookieCollectionTemplate('runOrderScripts', $event)"
					@load-cookie-scripts="loadCookieScripts"
					@show-manage-service="openManageServiceScreen"
				/>
				<CookieScriptRunOrderCard
					v-if="cookieCollectionTemplate.cookieScripts.length"
					:cookie-collection-template.sync="cookieCollectionTemplate"
					:all-cookie-scripts="cookieScripts"
					:user-can-create-update="userCanCreateUpdate && !(cookieCollectionTemplate.isSystem && !cloneMode)"
					@update:runOrderScripts="updateCookieCollectionTemplate('runOrderScripts', $event)"
				/>
			</template>
			<template #footer>
				<PageActionRow>
					<template #actions>
						<SecondaryActionButton @click="backToOverview">
							{{ backToOverviewText }}
						</SecondaryActionButton>
						<v-spacer />
						<PrimaryActionButton
							v-if="userCanCreateUpdate && !(cookieCollectionTemplate.isSystem && !cloneMode)"
							:disabled="mspaValidationFailed"
							@click="handleSubmit(submit)"
						>
							{{ isEdit ? 'Save' : 'Create' }}
						</PrimaryActionButton>
					</template>
				</PageActionRow>
			</template>
		</CookieTemplateLayout>
	</ValidationForm>
</template>

<script>
/* eslint-disable max-lines */
import { mapGetters } from 'vuex'
import CookieTemplateLayout from '../cookie-template-layout.vue'
import ValidationForm from '../../../../../../../shared/components/validation-form.vue'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import TextField from '../../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import Textarea from '../../../../../../../shared/components/textarea.vue'
import Toggle from '../../../../../../../shared/components/toggle.vue'
import CookieCategoriesCard from './cookie-categories-card.vue'
import { getTransactionTypes } from '../../../../../../../shared/utils/api/transaction-types.js'
import CookieScriptsInCategoryCard from './cookie-scripts-in-category-card.vue'
import CookieScriptRunOrderCard from './cookie-script-run-order-card.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../../shared/components/secondary-action-button.vue'
import PageActionRow from '../../../../../../../shared/components/page-action-row.vue'
import { singleBrand, defaultBrandInSelect, clientMultiBranded, brandOptions, UNIVERSAL_BRAND, UNIVERSAL_BRAND_ITEM, userHasBrand } from '../../../../../../../shared/state/brands.js'
import { getCookieScripts, getGtmConsentTypes } from '../../../../../../../shared/utils/api/cookies/cookie-scripts.js'
import { getCookieCategories } from '../../../../../../../shared/utils/api/cookies/cookie-categories.js'
import { postCookieCollectionTemplate, putCookieCollectionTemplate } from '../../../../../../../shared/utils/api/cookies/cookie-collection-templates.js'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'
import { COOKIE_COLLECTION_TEMPLATES } from '../../../../../router/route-names.js'
import { CAN_CREATE_UPDATE_COOKIE_COLLECTION_TEMPLATES } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { cassieIdentityServiceEnabled } from '../../../../../../../shared/state/config-keys.js'
import Message from '../../../../../../../shared/components/message.vue'
import ManageService from '../cookie-scripts/manage-service.vue'
import { validationObservers } from '../../../../../../../shared/state/validation-observers.js'
import { MSPA_ELEMENTS, MSPA_DEFAULT_CHANNEL_GUIDS } from '../../../../../enums/mspa-elements.js'
export default {
	components: {
		CookieTemplateLayout,
		ValidationForm,
		SectionCard,
		TextField,
		Dropdown,
		Textarea,
		CookieCategoriesCard,
		CookieScriptsInCategoryCard,
		CookieScriptRunOrderCard,
		PrimaryActionButton,
		SecondaryActionButton,
		PageActionRow,
		Toggle,
		Message,
		ManageService
	},
	props: {
		cookieCollectionTemplateToEdit: Object,
		userFullPermissions: Boolean,
		accessedVia: String,
		cloneMode: {
			type: Boolean,
			default: false
		}
	},
	setup () {
		return {
			userHasBrand,
			brandOptions,
			showSnackbar,
			cassieIdentityServiceEnabled,
			clientMultiBranded,
			singleBrand,
			defaultBrandInSelect
		}
	},
	data () {
		const ACCESSED_VIA_MANAGE_COOKIE_BANNER = 'manage-cookie-banner'
		const ACCESSED_VIA_COOOKIE_COLLECTION_TEMPLATES = 'cookie-collection-templates'
		return {
			cookieCollectionTemplate: JSON.parse(JSON.stringify(this.cookieCollectionTemplateToEdit || {
				name: '',
				brandId: null,
				description: '',
				cookieCategories: [],
				cookieScripts: [],
				runOrderScripts: [],
				crossDomainConsent: false,
				mspaEnabled: false
			})),
			cookieScripts: [],
			cookieCategories: [],
			transactionTypes: [],
			securityLevels: [],
			channelDeviceCopyActions: [],
			ACCESSED_VIA_MANAGE_COOKIE_BANNER,
			ACCESSED_VIA_COOOKIE_COLLECTION_TEMPLATES,
			showManageServiceScreen: false,
			serviceToEdit: null,
			selectedCategoryId: null,
			selectedCookieScriptChannelId: null,
			serviceJustAdded: false,
			gtmConsentTypes: []
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userCanCreateUpdate () {
			if (this.cookieCollectionTemplate.isSystem && !this.cloneMode) return false
			else if (this.isEdit) return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_COLLECTION_TEMPLATES) && userHasBrand(this.cookieCollectionTemplate.brandId)
			else return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_COLLECTION_TEMPLATES)
		},
		isEdit () {
			return !!this.cookieCollectionTemplateToEdit && !this.cloneMode
		},
		backToOverviewText () {
			if (this.accessedVia === this.ACCESSED_VIA_MANAGE_COOKIE_BANNER) return 'Back to Banner'
			else return 'Back to Overview'
		},
		mspaValidationFailed () {
			if (!this.cookieCollectionTemplate.mspaEnabled) return false
			const selected = this.cookieCollectionTemplate.cookieCategories.filter(category => category.isMspaCategory).length
			return selected !== 1
		},
		brandItems () {
			// ensures when viewing universal object it displays in dropdown
			const containsUniversal = brandOptions.value.map(brand => {
				return brand.value
			}).includes(UNIVERSAL_BRAND)
			if (containsUniversal || !this.isEdit) {
				return brandOptions.value
			} else {
				return [
					UNIVERSAL_BRAND_ITEM,
					...brandOptions.value
				]
			}
		},
		mspaCategoriesExist () {
			return this.cookieCategories.some(c => c.channelGuid)
		},
		gtmScriptsInUse () {
			// Create lookup maps for O(1) access instead of repeated .find() calls
			const scriptLookup = new Map(this.cookieScripts.map(s => [s.id, s]))
			const categoryLookup = new Map(this.cookieCategories.map(c => [c.categoryId, c]))
			const consentTypeLookup = new Map(this.gtmConsentTypes.map(g => [g.value, g]))
			return this.cookieCollectionTemplate.cookieScripts
				.map(script => {
					const fullScript = scriptLookup.get(script.id)
					if (!fullScript?.gtmEnabled) return null
					return {
						scriptId: script.id,
						brandId: fullScript.brandId,
						categoryId: script.categoryId,
						gtmConsentTypeId: fullScript.gtmConsentTypeId,
						gtmConsentType: consentTypeLookup.get(fullScript.gtmConsentTypeId)?.text,
						scriptName: fullScript.cookieScriptName,
						categoryName: categoryLookup.get(script.categoryId)?.categoryName,
						brandName: fullScript.brandName
					}
				})
				.filter(Boolean) // Remove null entries
		}
	},
	watch: {
		'cookieCollectionTemplate.mspaEnabled' (newVal) {
			if (newVal) {
				this.autoAddMspaCategoryWithServices()
			} else {
				// reset every category row
				this.cookieCollectionTemplate.cookieCategories =
        this.cookieCollectionTemplate.cookieCategories.map(row => ({ ...row, isMspaCategory: false }))
			}
		}
	},
	async created () {
		if (this.accessedVia === this.ACCESSED_VIA_MANAGE_COOKIE_BANNER) {
			this.scrollToTop()
		}
		await this.loadCookieScripts()
		await this.loadCookieCategories()
		await this.loadTransactionTypes()
		await this.loadGtmConsentTypes()
		if (this.cookieCollectionTemplate?.MSPAElements?.length) {
			this.cookieCollectionTemplate.cookieScripts = this.cookieCollectionTemplate.cookieScripts.map(script => {
				const matching = this.cookieCollectionTemplate.MSPAElements
					.filter(e => e.ServiceID === script.id)
					.map(e => e.MSPAElementID)
				return {
					...script,
					mspaElementIds: matching
				}
			})
		}
		if (!clientMultiBranded.value) {
			this.cookieCollectionTemplate.brandId = UNIVERSAL_BRAND
		}
		if (singleBrand.value) { this.cookieCollectionTemplate.brandId = defaultBrandInSelect.value }
	},
	methods: {
		assignedCookieCategory (cookieCategoryId) {
			return this.cookieCategories?.find(({ id }) => id === cookieCategoryId)
		},
		cookieCategoryScripts (categoryId) {
			let categoryScripts = this.cookieScripts?.filter(({ cookieCategoryId }) => cookieCategoryId === categoryId)
			categoryScripts = categoryScripts.map(({ cookieScriptName, brandId, brandName, id }) => ({
				text: `${cookieScriptName} - ${brandName}`,
				brandId,
				value: id,
				runOrder: null
			}))
			if (this.cookieCollectionTemplate.brandId === UNIVERSAL_BRAND) {
				categoryScripts = categoryScripts.filter(({ brandId }) => brandId === UNIVERSAL_BRAND)
			} else {
				categoryScripts = categoryScripts.filter(({ brandId }) => brandId === UNIVERSAL_BRAND || brandId === this.cookieCollectionTemplate.brandId)
			}
			return categoryScripts
		},
		filterMspaCategories () {
			const DEFAULT_MSPA_CATEGORY_GUID = 'A8140E74-58E3-4DF7-B98B-19F8FAD43148'
			return this.cookieCategories?.filter(c => c.channelGuid?.toUpperCase() === DEFAULT_MSPA_CATEGORY_GUID) || []
		},
		filterNonMspaRows (mspaCategories) {
			return this.cookieCollectionTemplate.cookieCategories
				.filter(r => !mspaCategories.some(c => c.id === r.id))
		},
		buildMspaCategoryRows (mspaCategories) {
			return mspaCategories.map(c => ({
				id: c.id,
				categoryId: c.id,
				isMspaCategory: true
			}))
		},
		filterMspaScripts (mspaCategories) {
			return this.cookieScripts.filter(s => mspaCategories.some(c => c.id === s.cookieCategoryId))
		},
		buildScriptRows (scripts) {
			const channelGuidToMspaElement = {
				[MSPA_DEFAULT_CHANNEL_GUIDS.SALE_OPT_OUT.toUpperCase()]: MSPA_ELEMENTS.SALE_OPT_OUT,
				[MSPA_DEFAULT_CHANNEL_GUIDS.SHARING_OPT_OUT.toUpperCase()]: MSPA_ELEMENTS.SHARING_OPT_OUT,
				[MSPA_DEFAULT_CHANNEL_GUIDS.TARGETED_ADVERTISING_OPT_OUT.toUpperCase()]: MSPA_ELEMENTS.TARGETED_ADVERTISING_OPT_OUT
			}
			return scripts.map(s => ({
				id: s.id,
				categoryId: s.cookieCategoryId,
				cookieCategoryId: s.cookieCategoryId,
				channelGuid: s.channelGuid,
				mspaElementIds: channelGuidToMspaElement[s.channelGuid?.toUpperCase()] ? [channelGuidToMspaElement[s.channelGuid?.toUpperCase()]] : []
			}))
		},
		filterNonMspaScripts (existingScripts, mspaScripts) {
			return existingScripts.filter(s => !mspaScripts.some(ms => ms.id === s.id))
		},
		autoAddMspaCategoryWithServices () {
			const mspaCats = this.filterMspaCategories()
			if (!mspaCats.length) return
			const currentRows = this.cookieCollectionTemplate.cookieCategories
			const strictlyIds = this.cookieCategories
				.filter(c => c.isStrictlyCategory)
				.map(c => c.id)
			const strictlyRows = currentRows.filter(r => strictlyIds.includes(r.id))
			const nonStrictNonMspa = currentRows.filter(r =>
				!strictlyIds.includes(r.id) &&
      !mspaCats.some(c => c.id === r.id)
			)
			const mspaRows = mspaCats.map(c => ({
				id: c.id,
				categoryId: c.id,
				isMspaCategory: true
			}))
			this.cookieCollectionTemplate.cookieCategories = [
				...strictlyRows,
				...mspaRows,
				...nonStrictNonMspa
			]
			const mspaScripts = this.filterMspaScripts(mspaCats)
			const newScriptRows = this.buildScriptRows(mspaScripts)
			this.cookieCollectionTemplate.cookieScripts = [
				...this.filterNonMspaScripts(this.cookieCollectionTemplate.cookieScripts, mspaScripts),
				...newScriptRows
			]
			this.cookieCollectionTemplate.runOrderScripts = [
				...this.filterNonMspaScripts(this.cookieCollectionTemplate.runOrderScripts, mspaScripts),
				...newScriptRows
			]
			this.$nextTick(() => this.$refs.validationForm?.validate())
		},
		assignedCookieCategoryScripts (categoryId) {
			return this.cookieCollectionTemplate.cookieScripts.filter(({ cookieCategoryId }) => cookieCategoryId === categoryId)
		},
		async loadCookieScripts () {
			const { cookieScripts } = await getCookieScripts()
			this.cookieScripts = cookieScripts
		},
		async loadCookieCategories () {
			const { cookieCategories } = await getCookieCategories()
			this.cookieCategories = cookieCategories
		},
		async loadTransactionTypes () {
			const { data: { transactionTypes, securityLevels, channelDeviceCopyActions } } = await getTransactionTypes()
			this.transactionTypes = transactionTypes
			this.securityLevels = securityLevels
			this.channelDeviceCopyActions = channelDeviceCopyActions
		},
		async loadGtmConsentTypes () {
			const { gtmConsentTypes } = await getGtmConsentTypes()
			this.gtmConsentTypes = gtmConsentTypes
		},
		updateCookieCollectionTemplate (property, value) {
			this.cookieCollectionTemplate = {
				...this.cookieCollectionTemplate,
				[property]: value
			}
		},
		scrollToTop () {
			if (this.accessedVia === this.ACCESSED_VIA_MANAGE_COOKIE_BANNER) document.querySelector('#manage_cookie_banner_container').scrollTo(0, 0)
		},
		backToOverview () {
			this.scrollToTop()
			if (this.accessedVia === this.ACCESSED_VIA_MANAGE_COOKIE_BANNER) {
				this.$emit('close')
			} else {
				this.$router.push({ name: COOKIE_COLLECTION_TEMPLATES })
			}
		},
		async submit () {
			const cookieCollectionTemplate = JSON.parse(JSON.stringify(this.cookieCollectionTemplate))
			cookieCollectionTemplate.cookieCategories = cookieCollectionTemplate.cookieCategories.map((category, index) => ({
				cookieCategoryId: category.id,
				displayOrder: index + 1,
				isMspaCategory: !!category.isMspaCategory
			}))
			cookieCollectionTemplate.cookieScripts = cookieCollectionTemplate.cookieScripts.map((script, index) => ({
				cookieScriptId: script.id,
				cookieCategoryId: script.categoryId,
				displayOrder: index + 1,
				runOrder: this.setScriptRunOrder(script.id),
				...(script.mspaElementIds && { mspaElementIds: script.mspaElementIds })
			}))
			cookieCollectionTemplate.MSPAElements = cookieCollectionTemplate.cookieScripts
				.filter(s => Array.isArray(s.mspaElementIds) && s.mspaElementIds.length)
				.flatMap(s => {
					return s.mspaElementIds.map(id => ({
						ServiceID: s.cookieScriptId,
						MSPAElementID: id
					}))
				})
			delete cookieCollectionTemplate.runOrderScripts
			if (!this.isEdit) {
				if (this.cloneMode) delete cookieCollectionTemplate.id
				await postCookieCollectionTemplate(cookieCollectionTemplate).then(response => {
					this.$emit('submit')
					this.$emit('get-new-collection-template-id', response.data.newCookieCollectionTemplateId)
				})
				this.showSnackbar('Collection template created')
			} else {
				delete cookieCollectionTemplate.usage
				delete cookieCollectionTemplate.brandName
				await putCookieCollectionTemplate(cookieCollectionTemplate)
				this.$emit('submit')
				this.showSnackbar('Collection template updated')
			}
			this.backToOverview()
		},
		setScriptRunOrder (id) {
			return this.cookieCollectionTemplate.runOrderScripts.findIndex(script => script.id === id) + 1
		},
		// Service management methods
		openManageServiceScreen (categoryId, serviceToEdit = null, cookieScriptChannelId = null) {
			this.selectedCategoryId = categoryId
			this.serviceToEdit = serviceToEdit
			this.selectedCookieScriptChannelId = cookieScriptChannelId
			this.showManageServiceScreen = true
		},
		closeManageServiceScreen () {
			this.showManageServiceScreen = false
			this.serviceToEdit = null
			this.selectedCategoryId = null
			this.selectedCookieScriptChannelId = null
		},
		clearValidationForCategory (categoryId) {
			// Clear validation errors directly from the global validation observer
			const validationId = `cookie-category-${categoryId}-scripts`
			if (validationObservers.value && validationObservers.value[0] && validationObservers.value[0].errors) {
				validationObservers.value[0].errors[validationId] = []
			}
			// Also find and trigger validation on the specific category card component
			const categoryCards = this.$children.filter(child => child.$options.name === 'CookieScriptsInCategoryCard' || child.$vnode?.tag?.includes('cookie-scripts-in-category-card'))
			const targetCard = categoryCards.find(card => card.cookieCategory?.categoryId === categoryId)
			if (targetCard && targetCard.validateCategoryScripts) {
				targetCard.validateCategoryScripts()
			}
		},
		async submitService (serviceData) {
			// Always reload cookie scripts to get the latest data
			await this.loadCookieScripts()

			if (serviceData && serviceData.id && serviceData.categoryId) {
				// Check if this service is already in the collection template
				const existingScript = this.cookieCollectionTemplate.cookieScripts.find(script => script.id === serviceData.id)
				if (!existingScript) {
					// Add the new service to the collection template
					const newScript = {
						id: serviceData.id,
						categoryId: serviceData.categoryId
					}
					// Use Vue.set to ensure reactivity
					this.$set(this.cookieCollectionTemplate, 'cookieScripts', [
						...this.cookieCollectionTemplate.cookieScripts,
						newScript
					])
					this.assignRunOrder(newScript)
					// Signal that a service was just added
					this.serviceJustAdded = true
					// Force validation update on next tick to ensure reactivity has processed
					await this.$nextTick()
					this.$forceUpdate()
					// Clear validation errors for the specific category
					await this.$nextTick()
					this.clearValidationForCategory(serviceData.categoryId)
					// Also trigger a full validation form refresh
					await this.$nextTick()
					if (this.$refs.validationForm && this.$refs.validationForm.$refs.validationObserver) {
						this.$refs.validationForm.$refs.validationObserver.validate()
					}
					// Reset the flag after a short delay
					setTimeout(() => {
						this.serviceJustAdded = false
					}, 100)
				}
			}
			this.closeManageServiceScreen()
		},
		assignRunOrder (script) {
			const runOrderScripts = [...this.cookieCollectionTemplate.runOrderScripts]
			const strictlyNecessaryScript = scriptId => {
				const foundScript = this.cookieScripts.find(({ id }) => id === scriptId)
				return foundScript?.cookieScriptType === 'Essential'
			}
			const runFirstScript = scriptId => {
				const foundScript = this.cookieScripts.find(({ id }) => id === scriptId)
				return foundScript?.isRunFirst
			}

			// Check if the script exists in our cookieScripts array
			const scriptExists = this.cookieScripts.find(({ id }) => id === script.id)
			if (!scriptExists) {
				console.warn(`Script with id ${script.id} not found in cookieScripts array. Skipping run order assignment.`)
				return
			}

			let insertAtIndex = 0
			const strictlyScripts = this.cookieCollectionTemplate.cookieScripts.map(script => ({
				...script,
				isStrictlyNecessary: strictlyNecessaryScript(script.id)
			})).filter(({ isStrictlyNecessary }) => isStrictlyNecessary)

			if (strictlyNecessaryScript(script.id) && runFirstScript(script.id)) {
				runOrderScripts.splice(insertAtIndex, 0, script)
			} else if (strictlyNecessaryScript(script.id) && !runFirstScript(script.id)) {
				insertAtIndex = strictlyScripts.length
				runOrderScripts.splice(insertAtIndex, 0, script)
			} else if (!strictlyNecessaryScript(script.id) && runFirstScript(script.id)) {
				insertAtIndex = strictlyScripts.length
				runOrderScripts.splice(insertAtIndex, 0, script)
			} else {
				runOrderScripts.push(script)
			}

			// Update the runOrderScripts with proper reactivity
			this.$set(this.cookieCollectionTemplate, 'runOrderScripts', runOrderScripts)
		}
	}
}
</script>
